/**
 * The Event Timing fields this package reads. `interactionId` is missing from the DOM
 * typings' `PerformanceEventTiming`, so it is declared here as optional, which also makes
 * a `PerformanceEntry` from `getEntries()` assignable without a cast.
 */
export type InteractionEntry = PerformanceEntry & { interactionId?: number };

/**
 * What an entry did to the interaction it belongs to: either it is the first entry of a new
 * interaction, or it measured an interaction that was already counted as slower than it was
 * known to be.
 */
export type InteractionUpdate =
	| { latencyMs: number; type: 'new' }
	| { fromMs: number; toMs: number; type: 'remeasured' };

/**
 * How many interactions are remembered, so their growth can still be applied. Entries of one
 * interaction arrive within the interaction itself, so anything older is not needed.
 */
const MAX_TRACKED = 256;
/**
 * How many are dropped per cleanup. Dropping one at a time would run a cleanup on every new
 * interaction once the limit is reached; a batch makes it one cleanup per 64 of them.
 */
const PRUNE_BATCH = 64;

/**
 * Groups Event Timing entries into interactions.
 *
 * Entries sharing a non-zero `interactionId` are one interaction whose latency is the
 * maximum `duration` among them. Entries arrive incrementally, so an interaction's latency
 * can grow after it was first reported — callers apply that to what they already counted
 * rather than counting the interaction twice.
 *
 * A non-zero `interactionId` is the browser's own definition of an interaction, which is
 * also what INP filters on: it is assigned to the pointer and keyboard events that make one
 * up, and never to scrolling or pointer movement.
 */
export class InteractionTracker {
	private latencies = new Map<number, number>();
	private readonly startsAfterInteractionId: number;
	private highestInteractionId = 0;

	/**
	 * @param startsAfterInteractionId interactions up to and including this one belong to the
	 * previous tracker and are ignored. `interactionId` counts up over the life of the page, so
	 * a session opening mid-page passes the highest id the one before it saw; without that, an
	 * entry still arriving for an interaction from the previous session would look new here and
	 * be counted in both.
	 */
	constructor(startsAfterInteractionId = 0) {
		this.startsAfterInteractionId = startsAfterInteractionId;
	}

	/** The highest `interactionId` this tracker has seen. */
	get lastInteractionId(): number {
		return Math.max(this.highestInteractionId, this.startsAfterInteractionId);
	}

	/**
	 * Merges an entry into the interaction it belongs to.
	 *
	 * @returns what that did to the interaction's latency, or nothing when the entry is not
	 * part of an interaction, belongs to a previous tracker, or does not change one.
	 */
	merge(entry: InteractionEntry): InteractionUpdate | undefined {
		const { interactionId } = entry;
		// `first-input` and non-interaction events report `interactionId` 0.
		if (!interactionId) {
			return undefined;
		}

		if (interactionId <= this.startsAfterInteractionId) {
			return undefined;
		}

		this.highestInteractionId = Math.max(this.highestInteractionId, interactionId);

		// Every latency is counted through here, so this is where one that cannot be measured
		// is rejected: a `NaN` getting through becomes a `NaN` bucket key and a `NaN` `sumMs`
		// for the rest of the session.
		if (!Number.isFinite(entry.duration) || entry.duration < 0) {
			return undefined;
		}

		const previousLatencyMs = this.latencies.get(interactionId);

		if (previousLatencyMs === undefined) {
			this.latencies.set(interactionId, entry.duration);
			this.prune();
			return { type: 'new', latencyMs: entry.duration };
		}

		if (entry.duration <= previousLatencyMs) {
			return undefined;
		}

		this.latencies.set(interactionId, entry.duration);
		return { type: 'remeasured', fromMs: previousLatencyMs, toMs: entry.duration };
	}

	private prune(): void {
		if (this.latencies.size <= MAX_TRACKED) {
			return;
		}

		// `Map` preserves insertion order and `interactionId` increases monotonically,
		// so the entries inserted first are the least likely to see another entry.
		let remaining = PRUNE_BATCH;
		for (const interactionId of this.latencies.keys()) {
			this.latencies.delete(interactionId);
			remaining -= 1;
			if (remaining === 0) {
				return;
			}
		}
	}
}
